import React, { useState, useEffect, useCallback } from 'react';
import './SupportSettings.css';

function SupportSettings({ apiBase, getToken }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(true);
  const [excludedEmails, setExcludedEmails] = useState([]);
  const [excludedDomains, setExcludedDomains] = useState([]);
  const [excludedKeywords, setExcludedKeywords] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [senderDomains, setSenderDomains] = useState(null);
  const [loadingDomains, setLoadingDomains] = useState(false);

  // Abandoned Checkout recovery template states
  const [emailFollowUpEnabled, setEmailFollowUpEnabled] = useState(false);
  const [emailFollowUpLogoUrl, setEmailFollowUpLogoUrl] = useState('');
  const [emailFollowUpBannerUrl, setEmailFollowUpBannerUrl] = useState('');
  const [emailFollowUpBannerLink, setEmailFollowUpBannerLink] = useState('');
  const [emailFollowUpPromoBannerUrl, setEmailFollowUpPromoBannerUrl] = useState('');
  const [emailFollowUpPromoBannerLink, setEmailFollowUpPromoBannerLink] = useState('');
  const [emailFollowUpSteps, setEmailFollowUpSteps] = useState({
    step1: { enabled: true, subject: '', headline: '', bodyText: '', buttonText: '', buttonColor: '#c28575', footerText: '' },
    step2: { enabled: true, subject: '', headline: '', bodyText: '', buttonText: '', buttonColor: '#3182ce', footerText: '' },
    step3: { enabled: true, subject: '', headline: '', bodyText: '', buttonText: '', buttonColor: '#2d3748', footerText: '' },
  });
  const [activeStepTab, setActiveStepTab] = useState('step1');
  const [activeSubTab, setActiveSubTab] = useState('general');

  // Meta Custom Audience states
  const [metaAudienceSyncEnabled, setMetaAudienceSyncEnabled] = useState(false);
  const [metaAccessToken, setMetaAccessToken] = useState('');
  const [metaCustomAudienceId, setMetaCustomAudienceId] = useState('');

  // Meta Conversions API states
  const [metaCapiEnabled, setMetaCapiEnabled] = useState(false);
  const [metaPixelId, setMetaPixelId] = useState('');
  const [metaCapiAccessToken, setMetaCapiAccessToken] = useState('');

  const updateActiveStepField = (field, value) => {
    setEmailFollowUpSteps(prev => ({
      ...prev,
      [activeStepTab]: {
        ...prev[activeStepTab],
        [field]: value
      }
    }));
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load settings');
      const data = await res.json();
      const s = data.settings || {};
      setSettings(s);
      setEmailEnabled(!!s.emailWorkerEnabled);
      setAutoReplyEnabled(s.autoReplyEnabled !== false);
      setExcludedEmails(s.excludedEmails || []);
      setExcludedDomains(s.excludedDomains || []);
      setExcludedKeywords(s.excludedKeywords || []);
      
      // Load checkout recovery settings
      setEmailFollowUpEnabled(!!s.emailFollowUpEnabled);
      setEmailFollowUpLogoUrl(s.emailFollowUpLogoUrl || '');
      setEmailFollowUpBannerUrl(s.emailFollowUpBannerUrl || '');
      setEmailFollowUpBannerLink(s.emailFollowUpBannerLink || '');
      setEmailFollowUpPromoBannerUrl(s.emailFollowUpPromoBannerUrl || '');
      setEmailFollowUpPromoBannerLink(s.emailFollowUpPromoBannerLink || '');
      if (s.emailFollowUpSteps) {
        setEmailFollowUpSteps(s.emailFollowUpSteps);
      }

      // Load Meta settings
      setMetaAudienceSyncEnabled(!!s.metaAudienceSyncEnabled);
      setMetaAccessToken(s.metaAccessToken || '');
      setMetaCustomAudienceId(s.metaCustomAudienceId || '');

      // Load Meta CAPI settings
      setMetaCapiEnabled(!!s.metaCapiEnabled);
      setMetaPixelId(s.metaPixelId || '');
      setMetaCapiAccessToken(s.metaCapiAccessToken || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, getToken]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailWorkerEnabled: emailEnabled,
          autoReplyEnabled,
          excludedEmails,
          excludedDomains,
          excludedKeywords,
          
          // Checkout recovery settings
          emailFollowUpEnabled,
          emailFollowUpLogoUrl,
          emailFollowUpBannerUrl,
          emailFollowUpBannerLink,
          emailFollowUpPromoBannerUrl,
          emailFollowUpPromoBannerLink,
          emailFollowUpSteps,

          // Meta Custom Audience settings
          metaAudienceSyncEnabled,
          metaAccessToken,
          metaCustomAudienceId,

          // Meta Conversions API settings
          metaCapiEnabled,
          metaPixelId,
          metaCapiAccessToken,
        }),
      });
      if (!res.ok) throw new Error('Failed to save settings');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addToList = (list, setList, value, setValue) => {
    const v = value.trim().toLowerCase();
    if (!v || list.includes(v)) return;
    setList([...list, v]);
    setValue('');
  };

  const removeFromList = (list, setList, value) => {
    setList(list.filter((item) => item !== value));
  };

  const scanInbox = async () => {
    setLoadingDomains(true);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/settings/sender-domains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to scan inbox');
      const data = await res.json();
      setSenderDomains(data.domains || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDomains(false);
    }
  };

  const excludeDomain = (domain) => {
    const d = domain.toLowerCase();
    if (!excludedDomains.includes(d)) {
      setExcludedDomains([...excludedDomains, d]);
    }
    setSenderDomains((prev) => prev.filter((s) => s.domain !== domain));
  };

  const excludeEmail = (email) => {
    const e = email.toLowerCase();
    if (!excludedEmails.includes(e)) {
      setExcludedEmails([...excludedEmails, e]);
    }
  };

  if (loading) return <div className="support-loading">Loading settings...</div>;
  if (error && !settings) return <div className="support-error">{error}</div>;

  return (
    <div className="settings-page">
      <div className="settings-header" style={{ marginBottom: '1rem' }}>
        <h2>Email Settings</h2>
        <div className="settings-header-actions">
          {saved && <span className="settings-saved">Saved</span>}
          {error && <span className="settings-save-error">{error}</span>}
          <button className="support-btn primary" onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Sub tabs selector */}
      <div className="support-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid #edf2f7', paddingBottom: '0.5rem' }}>
        <button
          type="button"
          className={`support-tab-btn ${activeSubTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('general')}
          style={{
            background: activeSubTab === 'general' ? '#edf2f7' : 'none',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            fontWeight: activeSubTab === 'general' ? '700' : 'normal',
            cursor: 'pointer'
          }}
        >
          General & Workers
        </button>
        <button
          type="button"
          className={`support-tab-btn ${activeSubTab === 'autoReply' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('autoReply')}
          style={{
            background: activeSubTab === 'autoReply' ? '#edf2f7' : 'none',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            fontWeight: activeSubTab === 'autoReply' ? '700' : 'normal',
            cursor: 'pointer'
          }}
        >
          Auto-Reply Template
        </button>
        <button
          type="button"
          className={`support-tab-btn ${activeSubTab === 'recovery' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('recovery')}
          style={{
            background: activeSubTab === 'recovery' ? '#edf2f7' : 'none',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            fontWeight: activeSubTab === 'recovery' ? '700' : 'normal',
            cursor: 'pointer'
          }}
        >
          Checkout Recovery Sequence
        </button>
        <button
          type="button"
          className={`support-tab-btn ${activeSubTab === 'metaAudience' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('metaAudience')}
          style={{
            background: activeSubTab === 'metaAudience' ? '#edf2f7' : 'none',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            fontWeight: activeSubTab === 'metaAudience' ? '700' : 'normal',
            cursor: 'pointer'
          }}
        >
          Meta Custom Audience
        </button>
        <button
          type="button"
          className={`support-tab-btn ${activeSubTab === 'metaCapi' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('metaCapi')}
          style={{
            background: activeSubTab === 'metaCapi' ? '#edf2f7' : 'none',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            fontWeight: activeSubTab === 'metaCapi' ? '700' : 'normal',
            cursor: 'pointer'
          }}
        >
          Meta Conversions API
        </button>
      </div>

      {/* General Settings Tab */}
      {activeSubTab === 'general' && (
        <>
          <div className="settings-section support-card">
            <div className="settings-section-title">Email Worker</div>
            <div className="settings-toggle-row">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                />
                <span className="settings-toggle-slider" />
              </label>
              <div>
                <div className="settings-toggle-label">Auto-create tickets from incoming emails</div>
                <div className="settings-toggle-desc">
                  Polls incoming emails every 2 minutes (support@, wholesale@, returns@, orders@) and creates support tickets automatically
                </div>
              </div>
            </div>
          </div>

          <div className="settings-section support-card">
            <div className="settings-section-title">Excluded Email Addresses</div>
            <div className="settings-section-desc">
              Emails from these addresses will be ignored and no ticket will be created
            </div>

            <div className="settings-list-input">
              <input
                type="email"
                className="support-input"
                placeholder="e.g. newsletter@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList(excludedEmails, setExcludedEmails, newEmail, setNewEmail))}
              />
              <button
                className="support-btn small"
                onClick={() => addToList(excludedEmails, setExcludedEmails, newEmail, setNewEmail)}
              >
                Add
              </button>
            </div>

            {excludedEmails.length > 0 && (
              <div className="settings-tags">
                {excludedEmails.map((email) => (
                  <span key={email} className="settings-tag">
                    {email}
                    <button
                      className="settings-tag-remove"
                      onClick={() => removeFromList(excludedEmails, setExcludedEmails, email)}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="settings-section support-card">
            <div className="settings-section-title">Excluded Domains</div>
            <div className="settings-section-desc">
              All emails from these domains will be ignored (e.g. skip all emails from @promotions.com)
            </div>

            <div className="settings-list-input">
              <input
                type="text"
                className="support-input"
                placeholder="e.g. promotions.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList(excludedDomains, setExcludedDomains, newDomain, setNewDomain))}
              />
              <button
                className="support-btn small"
                onClick={() => addToList(excludedDomains, setExcludedDomains, newDomain, setNewDomain)}
              >
                Add
              </button>
            </div>

            {excludedDomains.length > 0 && (
              <div className="settings-tags">
                {excludedDomains.map((domain) => (
                  <span key={domain} className="settings-tag">
                    @{domain}
                    <button
                      className="settings-tag-remove"
                      onClick={() => removeFromList(excludedDomains, setExcludedDomains, domain)}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="settings-section support-card">
            <div className="settings-section-title">Scan Inbox Senders</div>
            <div className="settings-section-desc">
              Scan your inbox to see all sender domains. Then choose which to exclude or allow.
            </div>
            <button
              className="support-btn small"
              onClick={scanInbox}
              disabled={loadingDomains}
            >
              {loadingDomains ? 'Scanning...' : 'Scan Inbox'}
            </button>

            {senderDomains && senderDomains.length === 0 && (
              <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>No senders found or all already excluded.</p>
            )}

            {senderDomains && senderDomains.length > 0 && (
              <div className="settings-domain-list">
                {senderDomains.map((s) => (
                  <div key={s.domain} className="settings-domain-row">
                    <div className="settings-domain-info">
                      <span className="settings-domain-name">@{s.domain}</span>
                      <span className="settings-domain-count">{s.count} email{s.count > 1 ? 's' : ''}</span>
                      <div className="settings-domain-emails">
                        {s.emails.slice(0, 5).map((e) => (
                          <span key={e} className="settings-domain-email">{e}</span>
                        ))}
                        {s.emails.length > 5 && <span className="settings-domain-email">+{s.emails.length - 5} more</span>}
                      </div>
                    </div>
                    <div className="settings-domain-actions">
                      <button
                        className="support-btn small danger"
                        onClick={() => excludeDomain(s.domain)}
                        title="Exclude entire domain"
                      >
                        Exclude Domain
                      </button>
                      {s.emails.length === 1 && (
                        <button
                          className="support-btn small"
                          onClick={() => excludeEmail(s.emails[0])}
                          title="Exclude just this email"
                        >
                          Exclude Email
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="settings-section support-card">
            <div className="settings-section-title">Excluded Subject Keywords</div>
            <div className="settings-section-desc">
              Emails with these words in the subject line will be ignored (e.g. "unsubscribe", "out of office")
            </div>

            <div className="settings-list-input">
              <input
                type="text"
                className="support-input"
                placeholder="e.g. unsubscribe"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList(excludedKeywords, setExcludedKeywords, newKeyword, setNewKeyword))}
              />
              <button
                className="support-btn small"
                onClick={() => addToList(excludedKeywords, setExcludedKeywords, newKeyword, setNewKeyword)}
              >
                Add
              </button>
            </div>

            {excludedKeywords.length > 0 && (
              <div className="settings-tags">
                {excludedKeywords.map((kw) => (
                  <span key={kw} className="settings-tag">
                    {kw}
                    <button
                      className="settings-tag-remove"
                      onClick={() => removeFromList(excludedKeywords, setExcludedKeywords, kw)}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Auto Reply Tab */}
      {activeSubTab === 'autoReply' && (
        <>
          <div className="settings-section support-card">
            <div className="settings-section-title">Auto-Reply Configuration</div>
            <div className="settings-toggle-row">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={autoReplyEnabled}
                  onChange={(e) => setAutoReplyEnabled(e.target.checked)}
                />
                <span className="settings-toggle-slider" />
              </label>
              <div>
                <div className="settings-toggle-label">Send auto-reply with ticket number</div>
                <div className="settings-toggle-desc">
                  Automatically sends confirmation email with ticket number when a new ticket is created from email
                </div>
              </div>
            </div>
          </div>

          <div className="settings-section support-card">
            <div className="settings-section-title">Auto-Reply Preview</div>
            <div className="settings-preview">
              <div className="settings-preview-header">
                <img src="https://www.fabvoguestudio.com/cdn/shop/files/logo_white_r.png?height=128&v=1776018706" alt="Fab Vogue Studio" className="settings-preview-logo" />
              </div>
              <div className="settings-preview-body">
                <p>Hi [Customer Name],</p>
                <p>Thank you for reaching out. We've received your message and created a support ticket.</p>
                <div className="settings-preview-ticket">
                  <div className="settings-preview-ticket-label">Your Ticket Number</div>
                  <div className="settings-preview-ticket-number">TICK-0001</div>
                </div>
                <p>Our team will review your request and get back to you shortly. Please include <strong>[TICK-0001]</strong> in the subject line when replying.</p>
                <p className="settings-preview-footer">Best regards,<br />Fab Vogue Studio Support Team</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Recovery Tab */}
      {activeSubTab === 'recovery' && (
        <div className="settings-section support-card">
          <div className="settings-section-title">Razorpay Checkout Recovery Email Sequence Settings</div>
          <div className="settings-section-desc">
            Configure a 3-step automated email follow-up sequence to recover abandoned Razorpay checkouts.
          </div>

          {/* Common configuration fields */}
          <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '6px', marginBottom: '1.25rem', border: '1px solid #edf2f7' }}>
            <div className="settings-toggle-row" style={{ marginBottom: '1.25rem' }}>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={emailFollowUpEnabled}
                  onChange={(e) => setEmailFollowUpEnabled(e.target.checked)}
                />
                <span className="settings-toggle-slider" />
              </label>
              <div>
                <div className="settings-toggle-label">Enable Automated Recovery Emails Sequence</div>
                <div className="settings-toggle-desc">
                  Automatically send automated follow-up emails to recovery-eligible Razorpay checkouts.
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="support-form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                <label>Brand Logo URL (Shared across all emails)</label>
                <input
                  type="text"
                  className="support-input"
                  value={emailFollowUpLogoUrl}
                  onChange={(e) => setEmailFollowUpLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </div>
              
              <div className="support-form-group" style={{ margin: 0 }}>
                <label>Top Header Banner Image URL</label>
                <input
                  type="text"
                  className="support-input"
                  value={emailFollowUpBannerUrl}
                  onChange={(e) => setEmailFollowUpBannerUrl(e.target.value)}
                  placeholder="e.g. https://example.com/header-banner.jpg"
                />
              </div>
              <div className="support-form-group" style={{ margin: 0 }}>
                <label>Top Banner Redirect Link</label>
                <input
                  type="text"
                  className="support-input"
                  value={emailFollowUpBannerLink}
                  onChange={(e) => setEmailFollowUpBannerLink(e.target.value)}
                  placeholder="e.g. https://www.fabvoguestudio.com/collections/offers"
                />
              </div>

              <div className="support-form-group" style={{ margin: 0 }}>
                <label>Bottom Collection Banner Image URL</label>
                <input
                  type="text"
                  className="support-input"
                  value={emailFollowUpPromoBannerUrl}
                  onChange={(e) => setEmailFollowUpPromoBannerUrl(e.target.value)}
                  placeholder="e.g. https://example.com/collection-promo.jpg"
                />
              </div>
              <div className="support-form-group" style={{ margin: 0 }}>
                <label>Collection Redirect Link</label>
                <input
                  type="text"
                  className="support-input"
                  value={emailFollowUpPromoBannerLink}
                  onChange={(e) => setEmailFollowUpPromoBannerLink(e.target.value)}
                  placeholder="e.g. https://www.fabvoguestudio.com/collections/best-sellers"
                />
              </div>
            </div>
          </div>

          {/* Step Selector Tabs */}
          <div className="recovery-steps-tabs" style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.25rem', borderBottom: '1px solid #edf2f7', paddingBottom: '0.5rem' }}>
            <button
              type="button"
              className={`support-btn ${activeStepTab === 'step1' ? 'primary' : 'secondary'}`}
              style={{ padding: '0.4rem 0.85rem', fontSize: '0.8rem', borderRadius: '20px' }}
              onClick={() => setActiveStepTab('step1')}
            >
              Email 1: Reminder (30m delay)
            </button>
            <button
              type="button"
              className={`support-btn ${activeStepTab === 'step2' ? 'primary' : 'secondary'}`}
              style={{ padding: '0.4rem 0.85rem', fontSize: '0.8rem', borderRadius: '20px' }}
              onClick={() => setActiveStepTab('step2')}
            >
              Email 2: Discount (12h wait)
            </button>
            <button
              type="button"
              className={`support-btn ${activeStepTab === 'step3' ? 'primary' : 'secondary'}`}
              style={{ padding: '0.4rem 0.85rem', fontSize: '0.8rem', borderRadius: '20px' }}
              onClick={() => setActiveStepTab('step3')}
            >
              Email 3: Expiration (24h wait)
            </button>
          </div>

          {/* Active Step Editor & Preview */}
          {(() => {
            const activeStepConfig = emailFollowUpSteps[activeStepTab] || {
              enabled: true, subject: '', headline: '', bodyText: '', buttonText: '', buttonColor: '#c28575', footerText: ''
            };

            return (
              <div className="settings-split-editor">
                {/* Left Column: Form Settings */}
                <div className="editor-form-panel">
                  <div className="settings-toggle-row" style={{ marginBottom: '1rem', background: '#edf2f7', padding: '0.75rem', borderRadius: '4px' }}>
                    <label className="settings-toggle">
                      <input
                        type="checkbox"
                        checked={activeStepConfig.enabled !== false}
                        onChange={(e) => updateActiveStepField('enabled', e.target.checked)}
                      />
                      <span className="settings-toggle-slider" />
                    </label>
                    <div>
                      <div className="settings-toggle-label" style={{ fontSize: '0.85rem' }}>Enable this step in sequence</div>
                      <div className="settings-toggle-desc" style={{ fontSize: '0.75rem' }}>
                        Disable this step to skip sending it and automatically progress checkouts to the next step.
                      </div>
                    </div>
                  </div>

                  <div className="support-form-group">
                    <label>Email Subject Line</label>
                    <input
                      type="text"
                      className="support-input"
                      value={activeStepConfig.subject || ''}
                      onChange={(e) => updateActiveStepField('subject', e.target.value)}
                      placeholder="e.g. Did You Forget Something Stylish?"
                    />
                  </div>

                  <div className="support-form-group">
                    <label>Email Headline</label>
                    <input
                      type="text"
                      className="support-input"
                      value={activeStepConfig.headline || ''}
                      onChange={(e) => updateActiveStepField('headline', e.target.value)}
                      placeholder="e.g. Did You Forget Something Stylish?"
                    />
                  </div>

                  <div className="support-form-group">
                    <label>Email Body Text (Use [Customer Name] to customize)</label>
                    <textarea
                      className="support-textarea"
                      style={{ height: '100px' }}
                      value={activeStepConfig.bodyText || ''}
                      onChange={(e) => updateActiveStepField('bodyText', e.target.value)}
                      placeholder="e.g. We noticed you left some beautiful items in your cart..."
                    />
                  </div>

                  <div className="support-form-row">
                    <div className="support-form-group" style={{ flex: 2 }}>
                      <label>CTA Button Text</label>
                      <input
                        type="text"
                        className="support-input"
                        value={activeStepConfig.buttonText || ''}
                        onChange={(e) => updateActiveStepField('buttonText', e.target.value)}
                        placeholder="e.g. RETURN TO YOUR CART & CHECKOUT"
                      />
                    </div>
                    <div className="support-form-group" style={{ flex: 1 }}>
                      <label>CTA Button Color</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="color"
                          className="support-input color-picker-input"
                          value={activeStepConfig.buttonColor || '#c28575'}
                          onChange={(e) => updateActiveStepField('buttonColor', e.target.value)}
                          style={{ padding: '2px', height: '38px', width: '50px', cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          className="support-input"
                          value={activeStepConfig.buttonColor || ''}
                          onChange={(e) => updateActiveStepField('buttonColor', e.target.value)}
                          style={{ fontFamily: 'monospace', width: '90px' }}
                          placeholder="#c28575"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="support-form-group">
                    <label>Email Footer Text</label>
                    <textarea
                      className="support-textarea"
                      style={{ height: '70px' }}
                      value={activeStepConfig.footerText || ''}
                      onChange={(e) => updateActiveStepField('footerText', e.target.value)}
                      placeholder="Need help? Simply reply to this email..."
                    />
                  </div>
                </div>

                {/* Right Column: Live Email Preview Panel */}
                <div className="editor-preview-panel">
                  <div className="preview-label">Live Email Preview ({activeStepTab.toUpperCase()})</div>
                  <div className="checkout-recovery-preview">
                    <div className="preview-header">
                      <img
                        src={emailFollowUpLogoUrl || 'https://www.fabvoguestudio.com/cdn/shop/files/logo_white_r.png?height=128&v=1776018706'}
                        alt="FAB VOGUE Studio"
                        className="preview-logo"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x50?text=Logo+Image';
                        }}
                      />
                    </div>

                    {/* Top Header Banner Preview */}
                    {emailFollowUpBannerUrl && (
                      <div style={{ borderBottom: '1px solid #edf2f7', textAlign: 'center' }}>
                        <img
                          src={emailFollowUpBannerUrl}
                          alt="Top Banner Preview"
                          style={{ width: '100%', height: 'auto', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}

                    <div className="preview-body">
                      <h3 className="preview-headline">{activeStepConfig.headline || 'Did You Forget Something Stylish?'}</h3>
                      <p className="preview-greeting">Hi John Doe,</p>
                      <div 
                        className="preview-text"
                        dangerouslySetInnerHTML={{
                          __html: (activeStepConfig.bodyText || "We noticed you left some beautiful items in your cart. They are still available, but they are looking for their perfect match! Here is a summary of what's waiting for you:")
                            .replace('[Customer Name]', 'John Doe')
                            .replace(/\n/g, '<br />')
                        }}
                      />

                      {/* Mock Cart Items Table */}
                      <table className="preview-cart-table">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}>Item</th>
                            <th>Description</th>
                            <th style={{ textAlign: 'center' }}>Qty</th>
                            <th style={{ textAlign: 'right' }}>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding: '8px 0', verticalAlign: 'middle' }}>
                              <img
                                src="https://www.fabvoguestudio.com/cdn/shop/files/logo_white_r.png?height=128&v=1776018706"
                                alt="Fabric"
                                style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '3px', border: '1px solid #edf2f7', display: 'block' }}
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/32?text=F'; }}
                              />
                            </td>
                            <td style={{ padding: '8px 0', paddingLeft: '8px' }}>
                              <strong>Royal Blue Silk Fabric</strong>
                              <div className="sku">SKU: SILK-BLU-01</div>
                            </td>
                            <td style={{ textAlign: 'center' }}>2x</td>
                            <td style={{ textAlign: 'right' }}>₹1,500</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px 0', verticalAlign: 'middle' }}>
                              <img
                                src="https://via.placeholder.com/32?text=Linen"
                                alt="Linen"
                                style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '3px', border: '1px solid #edf2f7', display: 'block' }}
                              />
                            </td>
                            <td style={{ padding: '8px 0', paddingLeft: '8px' }}>
                              <strong>Premium Floral Printed Linen</strong>
                              <div className="sku">SKU: LIN-FLR-04</div>
                            </td>
                            <td style={{ textAlign: 'center' }}>1x</td>
                            <td style={{ textAlign: 'right' }}>₹2,200</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="preview-total-row">
                        <span>Total: <strong>₹5,200</strong></span>
                      </div>

                      <div className="preview-cta-wrapper">
                        <button
                          type="button"
                          className="preview-cta-btn"
                          style={{ backgroundColor: activeStepConfig.buttonColor || '#c28575' }}
                        >
                          {activeStepConfig.buttonText || 'RETURN TO YOUR CART & CHECKOUT'}
                        </button>
                      </div>

                      {/* Bottom Promo Banner Preview */}
                      {emailFollowUpPromoBannerUrl && (
                        <div style={{ textAlign: 'center', marginTop: '24px', borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>
                          <div style={{ fontSize: '9px', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', fontWeight: 'bold' }}>Recommended Collection</div>
                          <img
                            src={emailFollowUpPromoBannerUrl}
                            alt="Promo Banner Preview"
                            style={{ width: '100%', height: 'auto', borderRadius: '4px', display: 'block' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="preview-footer">
                      <p dangerouslySetInnerHTML={{
                        __html: (activeStepConfig.footerText || 'Need help with your order? Simply reply to this email or message us on WhatsApp!')
                          .replace(/\n/g, '<br />')
                      }} />
                      <p className="sub-footer">© {new Date().getFullYear()} FAB VOGUE Studio. All Rights Reserved.</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Meta Audience Settings Tab */}
      {activeSubTab === 'metaAudience' && (
        <>
          <div className="settings-section support-card">
            <div className="settings-section-title">Meta Custom Audience Integration</div>
            <div className="settings-section-desc">
              Synchronize abandoned checkouts automatically with a Meta (Facebook) Custom Audience to target them with Ads.
            </div>

            <div className="settings-toggle-row" style={{ marginBottom: '1.5rem' }}>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={metaAudienceSyncEnabled}
                  onChange={(e) => setMetaAudienceSyncEnabled(e.target.checked)}
                />
                <span className="settings-toggle-slider" />
              </label>
              <div>
                <div className="settings-toggle-label">Enable Meta Custom Audience Sync</div>
                <div className="settings-toggle-desc">
                  Automatically add active checkouts to Meta and remove them when Converted or Ignored.
                </div>
              </div>
            </div>

            <div className="support-form-group" style={{ marginBottom: '1.25rem' }}>
              <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#4a5568' }}>Meta System User Access Token</label>
              <input
                type="password"
                className="support-input"
                value={metaAccessToken}
                onChange={(e) => setMetaAccessToken(e.target.value)}
                placeholder="EAAG..."
                style={{ width: '100%', marginTop: '0.25rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem' }}>
                Requires a Permanent System User Token with <code>ads_management</code> permissions from your Meta Business Suite.
              </div>
            </div>

            <div className="support-form-group" style={{ marginBottom: '1.25rem' }}>
              <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#4a5568' }}>Meta Custom Audience ID</label>
              <input
                type="text"
                className="support-input"
                value={metaCustomAudienceId}
                onChange={(e) => setMetaCustomAudienceId(e.target.value)}
                placeholder="e.g. 1202058473852901"
                style={{ width: '100%', marginTop: '0.25rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem' }}>
                Find this ID in Meta Events Manager under <strong>Audiences</strong>.
              </div>
            </div>
          </div>

          <div className="settings-section support-card" style={{ background: '#f7fafc', border: '1px solid #edf2f7', padding: '1rem', borderRadius: '8px' }}>
            <div className="settings-section-title" style={{ fontSize: '0.85rem', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              ℹ️ How does the synchronization work?
            </div>
            <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0, fontSize: '0.85rem', color: '#718096', lineHeight: '1.5' }}>
              <li><strong>Real-time Deletion:</strong> The moment a support agent marks a checkout as Converted or Ignored, the customer is immediately deleted from the Meta audience to save ad spend.</li>
              <li><strong>Real-time Addition:</strong> When checkouts are created or updated, they are added to the Meta target audience.</li>
              <li><strong>Reconciliation:</strong> A daily background job runs at 2:00 AM IST to reconcile all checkouts from the last 30 days and sync them with Meta.</li>
            </ul>
          </div>
        </>
      )}

      {/* Meta Conversions API Settings Tab */}
      {activeSubTab === 'metaCapi' && (
        <>
          <div className="settings-section support-card">
            <div className="settings-section-title">Meta Conversions API Integration</div>
            <div className="settings-section-desc">
              Send server-side Purchase events for both online (Shopify) and offline (Zoho call orders) transactions to maximize ad attribution.
            </div>

            <div className="settings-toggle-row" style={{ marginBottom: '1.5rem' }}>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={metaCapiEnabled}
                  onChange={(e) => setMetaCapiEnabled(e.target.checked)}
                />
                <span className="settings-toggle-slider" />
              </label>
              <div>
                <div className="settings-toggle-label">Enable Meta Conversions API Sync</div>
                <div className="settings-toggle-desc">
                  Automatically sync purchase events to Meta Conversions API (CAPI).
                </div>
              </div>
            </div>

            <div className="support-form-group" style={{ marginBottom: '1.25rem' }}>
              <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#4a5568' }}>Meta Pixel ID</label>
              <input
                type="text"
                className="support-input"
                value={metaPixelId}
                onChange={(e) => setMetaPixelId(e.target.value)}
                placeholder="e.g. 195762241651634"
                style={{ width: '100%', marginTop: '0.25rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem' }}>
                Your Meta Pixel ID where purchase events will be sent.
              </div>
            </div>

            <div className="support-form-group" style={{ marginBottom: '1.25rem' }}>
              <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#4a5568' }}>Conversions API Access Token</label>
              <input
                type="password"
                className="support-input"
                value={metaCapiAccessToken}
                onChange={(e) => setMetaCapiAccessToken(e.target.value)}
                placeholder="EAAG..."
                style={{ width: '100%', marginTop: '0.25rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem' }}>
                A permanent system user access token with Conversions API permissions. If left blank, it will fall back to your Custom Audience access token.
              </div>
            </div>
          </div>

          <div className="settings-section support-card" style={{ background: '#f7fafc', border: '1px solid #edf2f7', padding: '1rem', borderRadius: '8px' }}>
            <div className="settings-section-title" style={{ fontSize: '0.85rem', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              ℹ️ How does this integration work?
            </div>
            <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0, fontSize: '0.85rem', color: '#718096', lineHeight: '1.5' }}>
              <li><strong>Shopify Deduplication:</strong> Online orders will be formatted with a custom event ID (Order Name + "00") to cleanly match and deduplicate against the frontend pixel event.</li>
              <li><strong>Zoho Call Orders:</strong> Offline orders taken by call will be routed with an action source of <code>"phone_call"</code> and their reference number as the event ID.</li>
              <li><strong>Real-time Sync:</strong> Events are dispatched asynchronously as soon as orders are ingested or mirrored in the database, without blocking normal app operations.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default SupportSettings;
